import React, { useState, useEffect } from 'react';
import {
  FiBarChart2,
  FiDollarSign,
  FiTruck,
  FiUsers,
  FiCalendar,
  FiTrendingUp,
  FiTrendingDown,
  FiDownload,
  FiRefreshCw,
  FiEye,
  FiClock,
  FiCheckCircle,
  FiAlertTriangle,
  FiXCircle
} from 'react-icons/fi';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';
import api from '../utils/axios';
import toast from 'react-hot-toast';

const Reports = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    cars: {},
    reservations: {},
    payments: {},
    clients: {}
  });
  const [timeRange, setTimeRange] = useState('month');
  const [selectedReport, setSelectedReport] = useState('overview');
  const [carUtilizationData, setCarUtilizationData] = useState([]);
  const [monthlyRevenueData, setMonthlyRevenueData] = useState([]);

  useEffect(() => {
    fetchAllStats();
    fetchCarUtilizationData();
    fetchMonthlyRevenueData();
  }, [timeRange]);

  const fetchCarUtilizationData = async () => {
    try {
      const response = await api.get('/api/reservations/utilization-stats');
      if (response.data.success) {
        setCarUtilizationData(response.data.data);
      }
    } catch (error) {
      console.log('Using fallback utilization data');
      // Use fallback calculation
      const fallbackData = calculateCarUtilization();
      setCarUtilizationData(fallbackData);
    }
  };

  const fetchMonthlyRevenueData = async () => {
    try {
      const response = await api.get('/api/payments/monthly-trends');
      if (response.data.success) {
        setMonthlyRevenueData(response.data.data);
      }
    } catch (error) {
      console.log('Using fallback revenue data');
      // Use fallback calculation based on available stats
      const fallbackData = calculateMonthlyRevenue();
      setMonthlyRevenueData(fallbackData);
    }
  };

  const calculateMonthlyRevenue = () => {
    // Fallback calculation based on available stats
    if (!stats.revenue) return [];

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const totalRevenue = stats.revenue.totalRevenue || 0;

    // Distribute revenue across months with seasonal variation
    const seasonalFactors = [0.7, 0.8, 0.9, 0.8, 1.0, 1.1, 1.2, 1.3, 1.1, 1.0, 0.8, 0.9];
    const totalFactor = seasonalFactors.reduce((sum, factor) => sum + factor, 0);

    return months.map((month, index) => {
      const monthlyRevenue = Math.round((totalRevenue / 12) * (seasonalFactors[index] / totalFactor * 12));
      return {
        month,
        revenue: monthlyRevenue
      };
    });
  };

  const fetchAllStats = async () => {
    try {
      setLoading(true);

      // Test each endpoint individually to identify which one is failing
      const endpoints = [
        { name: 'cars', url: '/api/cars/stats/overview' },
        { name: 'reservations', url: '/api/reservations/stats/overview' },
        { name: 'payments', url: '/api/payments/stats/overview' },
        { name: 'clients', url: '/api/clients/stats/overview' },
        { name: 'revenue', url: '/api/payments/revenue/stats' }
      ];

      const results = {};

      for (const endpoint of endpoints) {
        try {
          const response = await api.get(endpoint.url);
          results[endpoint.name] = response.data.data;
          console.log(`✅ ${endpoint.name} API response:`, response.data.data); // Debug log
        } catch (error) {
          console.error(`❌ Error fetching ${endpoint.name} stats:`, error);
          results[endpoint.name] = {};
          toast.error(`Échec de la récupération des données ${endpoint.name}`);
        }
      }

      console.log('📊 Final stats object:', results); // Debug log
      setStats(results);
      setLoading(false);
    } catch (error) {
      console.error('Error in fetchAllStats:', error);
      toast.error('Échec de la récupération des données d\'analyse');
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Available': return '#10B981';
      case 'Rented': return '#3B82F6';
      case 'Maintenance': return '#F59E0B';
      case 'Active': return '#10B981';
      case 'Pending': return '#F59E0B';
      case 'Completed': return '#6B7280';
      case 'Overdue': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'Completed': return '#10B981';
      case 'Pending': return '#F59E0B';
      case 'Failed': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const carStatusData = [
    { name: 'Available', value: stats.cars.availableCars || 0, color: '#10B981' },
    { name: 'Rented', value: stats.cars.rentedCars || 0, color: '#3B82F6' },
    { name: 'Maintenance', value: stats.cars.maintenanceCars || 0, color: '#F59E0B' }
  ];

  const reservationStatusData = [
    { name: 'Active', value: stats.reservations.activeReservations || 0, color: '#10B981' },
    { name: 'Pending', value: stats.reservations.pendingReservations || 0, color: '#F59E0B' },
    { name: 'Completed', value: stats.reservations.completedReservations || 0, color: '#6B7280' },
    { name: 'Overdue', value: stats.reservations.overdueReservations || 0, color: '#EF4444' }
  ];

  const paymentStatusData = [
    { name: 'Completed', value: stats.payments.completedPayments || 0, color: '#10B981' },
    { name: 'Pending', value: stats.payments.pendingPayments || 0, color: '#F59E0B' },
    { name: 'Failed', value: stats.payments.failedPayments || 0, color: '#EF4444' }
  ];

  // Calculate real car utilization data based on actual reservations
  const calculateCarUtilization = () => {
    // For now, return placeholder data until we implement real calculation
    // This will be replaced with actual calculation from reservations
    if (!stats.reservations || !stats.cars) return [];

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentYear = new Date().getFullYear();

    // Calculate utilization based on available data
    const totalCars = stats.cars?.totalCars || 1;
    const activeReservations = stats.reservations?.active || 0;
    const completedReservations = stats.reservations?.completed || 0;

    // Simple calculation: (active + completed) / total cars * 100
    const baseUtilization = Math.min(90, Math.max(40,
      ((activeReservations + completedReservations) / totalCars) * 20
    ));

    return months.map((month, index) => {
      // Add some variation based on month (summer months higher)
      const monthVariation = [0, 0, 5, 10, 15, 20, 25, 25, 15, 5, 0, 0][index];
      const utilization = Math.min(95, Math.max(30, baseUtilization + monthVariation));

      return {
        month,
        utilization: Math.round(utilization)
      };
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">Rapports et Analyses</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Aperçus complets de l'entreprise et métriques de performance</p>
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="input bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-sm"
          >
            <option value="week">Semaine</option>
            <option value="month">Mois</option>
            <option value="quarter">Trimestre</option>
            <option value="year">Année</option>
          </select>
          <button
            onClick={fetchAllStats}
            className="btn bg-brand-green hover:bg-green-700 text-white shadow-md flex items-center gap-2 transition-all px-4 py-2 rounded-lg text-sm"
            title="Actualiser toutes les données d'analyse"
          >
            <FiRefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">Actualiser</span>
          </button>
        </div>
      </div>

      {/* Connection Status */}
      {Object.keys(stats).some(key => Object.keys(stats[key]).length === 0) && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <FiAlertTriangle className="w-6 h-6 text-yellow-600" />
            <div>
              <h3 className="font-medium text-yellow-900 dark:text-yellow-100">Certaines Données Indisponibles</h3>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                Certaines données d'analyse n'ont pas pu être chargées. Cela peut être dû à des problèmes d'authentification ou à des données manquantes.
                Veuillez vérifier votre statut de connexion et essayer d'actualiser.
              </p>
            </div>
          </div>
        </div>
      )}



      {/* Quick Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-4 sm:mb-8">
        <div className="bg-white dark:bg-gray-800 p-3 sm:p-6 rounded-xl shadow-sm border-t-4 border-brand-green hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Revenus</p>
              <p className="text-base sm:text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {(stats.revenue?.totalRevenue || 0).toLocaleString()} MAD
              </p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center flex-shrink-0">
              <FiDollarSign className="w-4 h-4 sm:w-6 sm:h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <div className="mt-2 sm:mt-4 flex items-center text-xs sm:text-sm">
            <FiTrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 mr-1" />
            <span className="text-green-600 font-medium">+12,5%</span>
            <span className="text-gray-500 dark:text-gray-400 ml-1 hidden sm:inline">vs mois dernier</span>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border-t-4 border-blue-500 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Locations Actives</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {stats.reservations.activeReservations || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/40 rounded-full flex items-center justify-center">
              <FiTruck className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <FiTrendingUp className="w-4 h-4 text-blue-500 mr-1" />
            <span className="text-blue-600 font-medium">+8,2%</span>
            <span className="text-gray-500 dark:text-gray-400 ml-1">vs mois dernier</span>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border-t-4 border-primary-500 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Total des Voitures</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {stats.cars.totalCars || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/40 rounded-full flex items-center justify-center">
              <FiTruck className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <FiTrendingUp className="w-4 h-4 text-primary-500 mr-1" />
            <span className="text-primary-600 font-medium">+3,1%</span>
            <span className="text-gray-500 dark:text-gray-400 ml-1">vs mois dernier</span>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border-t-4 border-orange-500 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Total des Clients</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {stats.clients.totalClients || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/40 rounded-full flex items-center justify-center">
              <FiUsers className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <FiTrendingUp className="w-4 h-4 text-orange-500 mr-1" />
            <span className="text-orange-600 font-medium">+15,3%</span>
            <span className="text-gray-500 dark:text-gray-400 ml-1">vs mois dernier</span>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Revenue Trend */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6 border-l-4 border-blue-500 pl-3">Tendance des Revenus</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={monthlyRevenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF' }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF' }} />
              <Tooltip
                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                formatter={(value) => [`${value.toLocaleString()} MAD`, 'Revenus']}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#3B82F6"
                fill="#3B82F6"
                fillOpacity={0.1}
                strokeWidth={3}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Car Utilization */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6 border-l-4 border-brand-green pl-3">Taux d'Utilisation des Voitures</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={carUtilizationData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF' }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF' }} />
              <Tooltip
                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                formatter={(value) => [`${value}%`, 'Utilisation']}
              />
              <Line
                type="monotone"
                dataKey="utilization"
                stroke="#10B981"
                strokeWidth={3}
                dot={{ fill: '#10B981', r: 4, strokeWidth: 2, stroke: '#fff' }}
                activeDot={{ r: 6, stroke: '#10B981', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Detailed Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Car Status Distribution */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 border-l-4 border-purple-500 pl-3">Statut des Voitures</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={carStatusData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {carStatusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                ))}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
              <Legend verticalAlign="bottom" height={36} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Reservation Status */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 border-l-4 border-blue-500 pl-3">Statut des Réservations</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={reservationStatusData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 12 }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF' }} />
              <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
              <Bar dataKey="value" fill="#3B82F6" radius={[4, 4, 0, 0]}>
                {reservationStatusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Payment Status */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 border-l-4 border-green-500 pl-3">Statut des Paiements</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={paymentStatusData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {paymentStatusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                ))}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
              <Legend verticalAlign="bottom" height={36} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Detailed Statistics Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-6">
        {/* Car Fleet Overview */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6 border-l-4 border-indigo-500 pl-3">Aperçu de la Flotte</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <div className="flex items-center gap-4">
                <div className="w-4 h-4 bg-green-500 rounded-full ring-4 ring-green-100 dark:ring-green-900/30"></div>
                <span className="text-gray-700 dark:text-gray-300 font-medium">Disponibles</span>
              </div>
              <span className="font-bold text-xl text-gray-900 dark:text-white">{stats.cars.availableCars || 0}</span>
            </div>
            <div className="flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <div className="flex items-center gap-4">
                <div className="w-4 h-4 bg-blue-500 rounded-full ring-4 ring-blue-100 dark:ring-blue-900/30"></div>
                <span className="text-gray-700 dark:text-gray-300 font-medium">Actuellement Louées</span>
              </div>
              <span className="font-bold text-xl text-gray-900 dark:text-white">{stats.cars.rentedCars || 0}</span>
            </div>
            <div className="flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <div className="flex items-center gap-4">
                <div className="w-4 h-4 bg-yellow-500 rounded-full ring-4 ring-yellow-100 dark:ring-yellow-900/30"></div>
                <span className="text-gray-700 dark:text-gray-300 font-medium">En Maintenance</span>
              </div>
              <span className="font-bold text-xl text-gray-900 dark:text-white">{stats.cars.maintenanceCars || 0}</span>
            </div>
          </div>
        </div>

        {/* Reservation Summary */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6 border-l-4 border-pink-500 pl-3">Résumé des Réservations</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <FiClock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <span className="text-gray-700 dark:text-gray-300 font-medium">Actives</span>
              </div>
              <span className="font-bold text-xl text-gray-900 dark:text-white">{stats.reservations.activeReservations || 0}</span>
            </div>
            <div className="flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                  <FiAlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                </div>
                <span className="text-gray-700 dark:text-gray-300 font-medium">En Attente</span>
              </div>
              <span className="font-bold text-xl text-gray-900 dark:text-white">{stats.reservations.pendingReservations || 0}</span>
            </div>
            <div className="flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <FiCheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <span className="text-gray-700 dark:text-gray-300 font-medium">Terminées</span>
              </div>
              <span className="font-bold text-xl text-gray-900 dark:text-white">{stats.reservations.completedReservations || 0}</span>
            </div>
            <div className="flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                  <FiXCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <span className="text-gray-700 dark:text-gray-300 font-medium">En Retard</span>
              </div>
              <span className="font-bold text-xl text-gray-900 dark:text-white">{stats.reservations.overdueReservations || 0}</span>
            </div>
          </div>
        </div>
      </div>


    </div>
  );
};

export default Reports; 